import { useEffect, useMemo, useState } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ClipboardList, Eye, Sparkles } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { Input } from '@reflex/ui/components/ui/input';
import { Label } from '@reflex/ui/components/ui/label';
import { Textarea } from '@reflex/ui/components/ui/textarea';
import { OwnerField } from '@reflex/ui/components/ownership/OwnerField';
import { cn } from '@reflex/ui/lib/utils';
import { PERSONA_WRITE } from '../shared/permissions.js';
import { PersonaIconPicker, PersonaIconPreview } from './PersonaIconPicker.js';
import {
  findRepoBlueprint,
  useAgentProviderKeyTarget,
  useAgentTypeRegistry,
  useAvailableBlueprints,
  useBlueprints,
  useSnapshots,
} from '@reflex/ui/client/hooks';
import { AgentConfigFormSection } from '@reflex/ui/components/agent/AgentConfigFormSection';
import { sanitizeAttachments, sanitizeEnvVars } from '@reflex/ui/client/agent-config-utils';
import {
  GIT_REPO_ATTACHMENT_ID,
  type GitRepoAttachmentConfig,
  type PluginAttachmentValue,
  type ResourceSize,
  type SandboxOptions,
} from '@runloop/reflex-contract';
import {
  ResourceOwnerInputSchema,
  type AgentEnvVar,
  type AgentType,
  type PromptMode,
  type ResourceOwnerInput,
} from '@reflex/shared';
import { PERSONA_FORM_DIALOG_FOOTER_CLASS } from './persona-form-layout.js';

const PERSONA_MISSING_KEY_ERROR = 'Choose a provider key to save this persona.';

const personaMetaFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  icon: z.string().nullable(),
  defaultPrompt: z.string().optional(),
  systemPrompt: z.string().optional(),
  owner: ResourceOwnerInputSchema,
});

export type PersonaMetaFormValues = z.infer<typeof personaMetaFormSchema>;

export function ownersDiffer(a: ResourceOwnerInput, b: ResourceOwnerInput): boolean {
  return a.ownerId !== b.ownerId;
}

export interface PersonaConfigDefaults {
  agentType: AgentType;
  promptMode: PromptMode | null;
  model: string;
  modelEndpointId: string | null;
  providerSecretId: string | null;
  blueprintName: string;
  snapshotId: string | null;
  resourceSize: ResourceSize | null;
  dockerd: boolean;
  idleTimeMinutes: number | null;
  attachments: PluginAttachmentValue[];
  envVars: AgentEnvVar[];
  /** Labels (`alb_*`) put on every run launched from this persona. */
  labelIds: string[];
  /** Group (`agp_*`) every run launched from this persona is filed into. */
  groupId: string | null;
  /**
   * Sandbox capabilities with no control in this form (computer use, artifact
   * mirrors, http resume). Carried through the form untouched and merged back
   * on submit so editing a persona — or saving one from a run that had them —
   * cannot silently drop them.
   */
  sandboxOptionsPassthrough?: SandboxOptions;
}

export interface PersonaSubmitPayload extends PersonaMetaFormValues, PersonaConfigDefaults {}

interface PersonaFormProps {
  metaDefaults: PersonaMetaFormValues;
  configDefaults: PersonaConfigDefaults;
  isPending: boolean;
  onSubmit: (payload: PersonaSubmitPayload) => void;
  submitLabel: string;
  contentClassName?: string;
  footerClassName?: string;
}

export function PersonaForm({
  metaDefaults,
  configDefaults,
  isPending,
  onSubmit,
  submitLabel,
  contentClassName,
  footerClassName,
}: PersonaFormProps) {
  const [agentType, setAgentType] = useState<AgentType>(configDefaults.agentType);
  const [promptMode, setPromptMode] = useState<PromptMode | null>(configDefaults.promptMode);
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);
  const [model, setModel] = useState<string>(configDefaults.model);
  const [modelEndpointId, setModelEndpointId] = useState<string | null>(
    configDefaults.modelEndpointId,
  );
  const [providerSecretId, setProviderSecretId] = useState<string | null>(
    configDefaults.providerSecretId,
  );
  const [missingKeySubmitAttempted, setMissingKeySubmitAttempted] = useState(false);
  const [manualBlueprintName, setManualBlueprintName] = useState<string | null>(
    configDefaults.blueprintName ? configDefaults.blueprintName : null,
  );
  const [manualSnapshotId, setManualSnapshotId] = useState<string | null>(
    configDefaults.snapshotId,
  );
  const { data: snapshots = [] } = useSnapshots();
  const [resourceSize, setResourceSize] = useState<ResourceSize | null>(
    configDefaults.resourceSize ?? null,
  );
  const [dockerd, setDockerd] = useState<boolean>(configDefaults.dockerd);
  const [idleTimeMinutes, setIdleTimeMinutes] = useState<number | null>(
    configDefaults.idleTimeMinutes,
  );
  const [attachmentValues, setAttachmentValues] = useState<Record<string, PluginAttachmentValue>>(
    () => {
      const map: Record<string, PluginAttachmentValue> = {};
      for (const att of configDefaults.attachments) {
        map[att.attachmentId] = att;
      }
      return map;
    },
  );
  const agentTypeRegistry = useAgentTypeRegistry();
  const initialReasoningEnvVar = agentTypeRegistry.getAgentType(
    configDefaults.agentType,
  )?.reasoningEnvVar;
  const [reasoningEffort, setReasoningEffort] = useState<string | null>(
    initialReasoningEnvVar
      ? (configDefaults.envVars.find((v) => v.key === initialReasoningEnvVar)?.value ?? null)
      : null,
  );
  const [envVars, setEnvVars] = useState<AgentEnvVar[]>(() =>
    configDefaults.envVars
      .filter((entry) => !initialReasoningEnvVar || entry.key !== initialReasoningEnvVar)
      .map((entry) => ({ ...entry })),
  );
  const hasStoredSecrets = configDefaults.envVars.some((v) => Boolean(v.secretRef));
  const [labelIds, setLabelIds] = useState<string[]>(configDefaults.labelIds);
  const [groupId, setGroupId] = useState<string | null>(configDefaults.groupId);

  const { data: blueprints = [] } = useBlueprints();
  const repoSlug = useMemo(() => {
    const entry = attachmentValues[GIT_REPO_ATTACHMENT_ID];
    const config = entry?.config as GitRepoAttachmentConfig | undefined;
    return config?.repoSlug ?? '';
  }, [attachmentValues]);
  const availableBlueprints = useAvailableBlueprints(blueprints, repoSlug);
  const autoResolvedBlueprintName = findRepoBlueprint(availableBlueprints, repoSlug)?.name ?? '';
  const displayedBlueprintName = manualBlueprintName ?? autoResolvedBlueprintName;

  const { keyTypes } = useAgentProviderKeyTarget(agentType, model, selectedProviderId);
  const requiresKey = keyTypes.length > 0;
  const missingRequiredKey = requiresKey && !providerSecretId;
  const showMissingKeyError = missingKeySubmitAttempted && missingRequiredKey;

  // Drop a stale key selection when switching to a free-tier provider so it
  // isn't persisted on save and the picker re-applies a remembered pick on
  // the way back to a keyed provider.
  useEffect(() => {
    if (!requiresKey && providerSecretId) {
      setProviderSecretId(null);
    }
  }, [requiresKey, providerSecretId]);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<PersonaMetaFormValues>({
    resolver: zodResolver(personaMetaFormSchema),
    defaultValues: metaDefaults,
  });

  const ownerValue = useWatch({ control, name: 'owner' }) ?? metaDefaults.owner;
  const providerKeyOwner = useMemo(
    () => (ownerValue.ownerId ? { ownerId: ownerValue.ownerId } : undefined),
    [ownerValue.ownerId],
  );

  function handleFormSubmit(data: PersonaMetaFormValues) {
    if (missingRequiredKey) {
      // Mirror launch: keep submit enabled, surface the missing-key state
      // inline next to the highlighted picker instead of a toast.
      setMissingKeySubmitAttempted(true);
      return;
    }
    const sanitizedEnvVars = sanitizeEnvVars(envVars);
    const info = agentTypeRegistry.getAgentType(agentType);
    if (reasoningEffort && info?.reasoningEnvVar) {
      sanitizedEnvVars.push({ key: info.reasoningEnvVar, value: reasoningEffort });
    }
    onSubmit({
      ...data,
      agentType,
      promptMode,
      model,
      modelEndpointId,
      providerSecretId: requiresKey ? providerSecretId : null,
      blueprintName: manualBlueprintName ?? '',
      snapshotId: manualSnapshotId,
      resourceSize,
      dockerd,
      idleTimeMinutes,
      attachments: sanitizeAttachments(attachmentValues),
      envVars: sanitizedEnvVars,
      labelIds,
      groupId,
      sandboxOptionsPassthrough: configDefaults.sandboxOptionsPassthrough,
    });
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="flex min-h-0 flex-1 flex-col">
      <div
        className={cn(
          'min-h-0 flex-1 space-y-4 overflow-y-auto px-4 pt-2 pb-4 md:px-6',
          contentClassName,
        )}
      >
        <div className="space-y-1.5">
          <Label htmlFor="persona-name">Name</Label>
          <div className="flex items-center gap-2">
            <Controller
              name="icon"
              control={control}
              render={({ field }) => (
                <PersonaIconPicker
                  value={field.value ?? null}
                  onChange={(next) => field.onChange(next)}
                  trigger={
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-9 w-9 shrink-0 text-base"
                      aria-label="Pick icon or emoji"
                    >
                      <PersonaIconPreview
                        icon={field.value}
                        fallback={
                          <Sparkles className="h-4 w-4 text-muted-foreground" aria-hidden />
                        }
                        className="h-4 w-4"
                      />
                    </Button>
                  }
                />
              )}
            />
            <Input
              id="persona-name"
              {...register('name')}
              placeholder="Grafana DevOps, Runloop CI Agent…"
              className="flex-1"
            />
          </div>
          {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="persona-description">
            Description <span className="font-normal text-muted-foreground">(optional)</span>
          </Label>
          <Input
            id="persona-description"
            {...register('description')}
            placeholder="Purpose-built agent for monitoring Grafana dashboards"
          />
        </div>

        <AgentConfigFormSection
          agentType={agentType}
          selectedProviderId={selectedProviderId}
          model={model}
          modelEndpointId={modelEndpointId}
          reasoningEffort={reasoningEffort}
          blueprintSelection={{ mode: 'name', value: displayedBlueprintName || '' }}
          snapshots={snapshots}
          snapshotId={manualSnapshotId}
          onSnapshotChange={setManualSnapshotId}
          resourceSize={resourceSize}
          dockerd={dockerd}
          idleTimeMinutes={idleTimeMinutes}
          attachmentValues={attachmentValues}
          envVars={envVars}
          hasStoredSecrets={hasStoredSecrets}
          labelIds={labelIds}
          onLabelIdsChange={setLabelIds}
          groupId={groupId}
          onGroupIdChange={setGroupId}
          providerSecretId={providerSecretId}
          onProviderSecretChange={setProviderSecretId}
          providerKeyOwner={providerKeyOwner}
          showMissingKeyError={showMissingKeyError}
          missingKeyErrorText={PERSONA_MISSING_KEY_ERROR}
          onResourceSizeChange={setResourceSize}
          onDockerdChange={setDockerd}
          onIdleTimeMinutesChange={setIdleTimeMinutes}
          onAgentTypeChange={(next) => {
            setAgentType(next);
            setSelectedProviderId(null);
            setModel('');
            setModelEndpointId(null);
            setProviderSecretId(null);
            setReasoningEffort(null);
          }}
          onModelChange={(modelId) => {
            setModel(modelId);
            // Clear any deliberate provider override so the matrix derives from
            // the newly selected model.
            setSelectedProviderId(null);
            setModelEndpointId(null);
          }}
          onReasoningEffortChange={(effort) => setReasoningEffort(effort)}
          onEndpointChange={(endpointId) => setModelEndpointId(endpointId)}
          onBlueprintChange={(next) => setManualBlueprintName(next ?? '')}
          onAttachmentChange={(id, config, pluginName) => {
            setAttachmentValues((prev) => {
              const next = { ...prev };
              if (config === null) {
                delete next[id];
              } else {
                next[id] = { attachmentId: id, pluginName, config };
              }
              return next;
            });
          }}
          onEnvVarsChange={setEnvVars}
        />

        <div className="space-y-1.5">
          <Label htmlFor="persona-system-prompt">
            System prompt <span className="font-normal text-muted-foreground">(optional)</span>
          </Label>
          <p className="text-xs text-muted-foreground">
            Custom instructions sent as the system prompt. When set, replaces the default mode-based
            system instructions.
          </p>
          <Textarea
            id="persona-system-prompt"
            {...register('systemPrompt')}
            placeholder="You are a DevOps agent specializing in Kubernetes deployments…"
            rows={3}
            className="field-sizing-fixed resize-y"
          />
        </div>

        <div className="space-y-1.5">
          <Label>Prompt mode</Label>
          <p className="text-xs text-muted-foreground">
            Controls how the agent interacts with the working tree when launched from this persona.
          </p>
          <div className="flex gap-2">
            <Button
              type="button"
              variant={!promptMode || promptMode === 'implement' ? 'secondary' : 'ghost'}
              size="xs"
              className="gap-1 text-xs"
              onClick={() => setPromptMode(null)}
            >
              <Sparkles className="icon-sm" />
              Implement
            </Button>
            <Button
              type="button"
              variant={promptMode === 'plan' ? 'secondary' : 'ghost'}
              size="xs"
              className="gap-1 text-xs"
              onClick={() => setPromptMode('plan')}
            >
              <ClipboardList className="icon-sm" />
              Plan
            </Button>
            <Button
              type="button"
              variant={promptMode === 'review' ? 'secondary' : 'ghost'}
              size="xs"
              className="gap-1 text-xs"
              onClick={() => setPromptMode('review')}
            >
              <Eye className="icon-sm" />
              Review
            </Button>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="persona-default-prompt">
            Default prompt <span className="font-normal text-muted-foreground">(optional)</span>
          </Label>
          <p className="text-xs text-muted-foreground">
            Pre-filled in the launch dialog — the user can edit it before starting.
          </p>
          <Textarea
            id="persona-default-prompt"
            {...register('defaultPrompt')}
            placeholder="Investigate the current alert and summarise the root cause…"
            rows={3}
          />
        </div>

        <OwnerField
          control={control}
          name="owner"
          organizationPermission={PERSONA_WRITE}
          description="Who can see and use this persona."
        />
      </div>

      <div className={cn(PERSONA_FORM_DIALOG_FOOTER_CLASS, footerClassName)}>
        <Button
          type="submit"
          size="sm"
          disabled={isPending}
          data-tutorial-id="persona-create-submit"
        >
          {isPending ? 'Saving…' : submitLabel}
        </Button>
      </div>
    </form>
  );
}
